import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  ListItem,
  Typography,
  styled,
  Box,
  LinearProgress,
  IconButton,
} from '@mui/material';
import { FileStatus } from '~/store/resource/resource.type';
import { pxToRem } from '~/theme/typography';

export const bookCard = {
  Container: styled(Card)(() => ({
    width: '242px',
    boxShadow: '0px 3.5px 10px -2.3px #B7BABD',
  })),
  Head: styled(CardHeader)(({ theme }) => ({
    backgroundColor: '#E7F4FF',
    color: theme.palette.text.secondary,
    padding: '12px 16px',
    height: '76px',
    userSelect: 'none',
    '& span': {
      fontSize: pxToRem(18),
      fontWeight: 500,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': '2',
      wordBreak: 'break-all',
    },
  })),
  Content: styled(CardContent)(() => ({
    padding: '8px 16px',
    position: 'relative',
  })),
  ProgressMask: styled(Box)(() => ({
    position: 'absolute',
    width: '209px',
    height: '238px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    color: 'white',
    display: 'flex',
    paddingTop: '72px',
    alignItems: 'center',
    flexDirection: 'column',
    fontSize: pxToRem(18),
  })),
  ProgressBar: styled(LinearProgress)(({ status }: { status: FileStatus }) => ({
    width: 'calc(100% - 16px)',
    margin: '16px 0px 8px 0px',
    backgroundColor: 'white',
    '& .MuiLinearProgress-bar': {
      backgroundColor: status === FileStatus.installing ? '#6BED98' : '#FFA235',
    },
  })),
  MacOnlyBadge: styled(Box)(() => ({
    position: 'absolute',
    left: '16px',
    bottom: '8px',
    width: 'calc(100% - 32px)',
    height: '56px',
    backgroundColor: 'rgba(22, 28, 36, 0.8)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    fontSize: pxToRem(30),
    fontWeight: 700,
    userSelect: 'none',
    pointerEvents: 'none',
  })),
  Action: styled(CardActions)(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 16px 12px',
    backgroundColor: 'white',
    // minHeight: '68px',
  })),
  MoreButton: styled(IconButton)(({ theme }) => ({
    top: '24px',
    right: '24px',
    position: 'absolute',
    width: '40px',
    height: '40px',
    border: 'solid 1px #D7C1A3',
    background: theme.palette.secondary.lighter,
    '&:hover': {
      background: '#D7C1A3',
    },
  })),
  Button: styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.secondary.light,
    fontSize: pxToRem(20),
    fontWeight: '700',
    padding: '8px 14px',
    '&:hover': {
      backgroundColor: theme.palette.secondary.darker,
      color: 'white',
    },
  })),
};

export const listCard = {
  Container: styled(Card)(() => ({
    width: '304px',
    height: '448px',
    boxShadow: '0px 3.5px 10px -2.3px #B7BABD',
  })),
  Head: styled(CardHeader)(({ theme }) => ({
    backgroundColor: theme.palette.secondary.lighter,
    color: theme.palette.text.secondary,
    padding: '12px 16px',
    height: '72px',
    display: '-webkit-box !important',
    '& span': {
      fontSize: pxToRem(18),
      fontWeight: 500,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': '2',
      wordBreak: 'break-all',
    },
  })),
  Content: styled(CardContent)(() => ({
    padding: '0px !important',
    height: '376px',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    display: 'flex',
    flexDirection: 'column',
  })),
  ListItemLabel: styled(Typography)(() => ({
    fontSize: pxToRem(18),
    lineHeight: 'normal',
  })),
  ListItem: styled(ListItem)(() => ({
    flexWrap: 'wrap',
    padding: '6px 12px 8px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  })),
  ProgressBar: styled(LinearProgress)(({ status }: { status: FileStatus }) => ({
    width: '215px',

    backgroundColor: 'white',
    '& .MuiLinearProgress-bar': {
      backgroundColor: status === FileStatus.installing ? '#6BED98' : '#FFA235',
    },
  })),
  Action: styled(Box)(() => ({
    zIndex: '2',
    marginTop: '8px',
    display: 'flex',
    gap: '8px',
  })),
  Button: styled(Button)<{ single: boolean }>(({ theme, single }) => ({
    backgroundColor: single
      ? theme.palette.secondary.lighter
      : theme.palette.secondary.light,
    width: '108px',
    height: '46px',
    marginRight: single ? '12px' : '0px',
    padding: '8px 14px',
    fontSize: pxToRem(20),
    fontWeight: '700',
    color: single
      ? theme.palette.secondary.dark
      : theme.palette.secondary.darker,
  })),
  downloadingContainer: styled(Box)(() => ({
    zIndex: '10',
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: '0px',
    left: '0px',
    padding: '8px 12px',
    backgroundColor: 'rgba(0,0,0,0.65)',
  })),
};

const DOWNLOAD_CARD = {
  ACTION_WIDTH: '390px',
  MAIN_GAP: '40px',
  SUB_GAP: '16px',
  IMAGE_SIZE: '72px',
};

export const downloadCard = {
  Card: styled(Card)(({ theme }) => ({
    width: '100%',
    padding: '22px 16px',
    borderRadius: '10px',
    border: '1px solid #A6A29D',
    display: 'flex',
    gap: DOWNLOAD_CARD.MAIN_GAP,
    [theme.breakpoints.down('desktop')]: {
      flexDirection: 'column',
    },
  })),
  Main: styled(Box)(({ theme }) => ({
    display: 'flex',
    width: `calc(100%  - ${DOWNLOAD_CARD.MAIN_GAP})`,
    gap: DOWNLOAD_CARD.SUB_GAP,
    [theme.breakpoints.down('desktop')]: {
      width: '100%',
    },
  })),
  Action: styled(Box)(({ theme }) => ({
    minWidth: '256px',
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('desktop')]: {
      minWidth: 'auto',
    },
  })),
  Info: styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: `calc(100% - ${DOWNLOAD_CARD.IMAGE_SIZE} - ${DOWNLOAD_CARD.SUB_GAP})`,
  })),
  Title: styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: pxToRem(20),
    fontWeight: '500',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('desktop')]: {
      textOverflow: 'clip',
      whiteSpace: 'normal',
      display: '-webkit-box',
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': '2',
    },
  })),
  Content: styled(Typography)<{ isProgressing: boolean }>(
    ({ isProgressing }) => ({
      fontSize: pxToRem(16),
      fontWeight: '500',
      color: '#908D89',
      marginBottom: isProgressing ? '8px' : '0px',
      display: 'flex',
      justifyContent: isProgressing ? 'space-between' : 'flex-start',
    }),
  ),
  Img: styled('img')(() => ({
    width: DOWNLOAD_CARD.IMAGE_SIZE,
    aspectRatio: '1/1',
  })),
};

export const stateCard = {
  Container: styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  })),
  Title: styled(Typography)(() => ({
    textAlign: 'center',
    fontSize: pxToRem(18),
    fontWeight: '700',
    lineHeight: '28px',
  })),
  Content: styled(Typography)(() => ({
    fontSize: pxToRem(16),
    fontWeight: '500',
  })),
  Img: styled('img')(({ theme }) => ({
    height: '200px',
    width: '200px',
    [theme.breakpoints.down('desktop')]: {
      height: '150px',
      width: '150px',
    },
  })),
};

export const internetCard = {
  Card: styled(Card)(() => ({
    backgroundColor: 'white',
    border: 'solid 1.5px #008A5D',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    cursor: 'pointer',
    color: '#005D51',
    '&:hover': {
      backgroundColor: '#E7F4FF',
      color: '#005D51',
    },
  })),
  IconWrapper: styled(Box)(() => ({
    backgroundColor: '#008A5D',
    padding: '10px',
    borderRadius: '8px',
    height: '44px',
  })),
  title: styled(Typography)(() => ({
    fontWeight: '500',
    fontSize: pxToRem(18),
  })),
};
